import {
  AfterViewInit,
  Component,
  ElementRef,
  OnDestroy,
  OnInit,
  ViewChild,
} from '@angular/core';
import { MatTableDataSource } from '@angular/material/table';
import { MatSort } from '@angular/material/sort';
import { SubSink } from 'subsink';
import { MatDialog } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { fromEvent, of } from 'rxjs';
import { debounceTime, map, switchAll, tap } from 'rxjs/operators';
import { SmallDialogComponent } from '../../../../atoms/small-dialog/small-dialog.component';
import { stringsList } from '../../../../constants/app-config';
import { TranslateService } from '@ngx-translate/core';
import { ChartOfAccountsDataService, ChartOfAccountsModel } from '../../services/chart-of-accounts-data.service';
import { ChartOfAccountsAddDialogComponent } from '../chart-of-accounts-add-dialog/chart-of-accounts-add-dialog.component';
import { ChartOfAccountsBulkImportComponent } from '../chart-of-accounts-bulk-import/chart-of-accounts-bulk-import.component';

@Component({
  selector: 'app-chart-of-accounts-table',
  templateUrl: './chart-of-accounts-table.component.html',
  styleUrls: ['./chart-of-accounts-table.component.css'],
})
export class ChartOfAccountsTableComponent implements OnInit, AfterViewInit, OnDestroy {
  displayedColumns: string[] = ['checkbox', 'konto', 'nazivKonta', 'actions'];
  selectedInvoicesNum: number = 0;
  private subs = new SubSink();
  dataSource!: MatTableDataSource<ChartOfAccountsModel>;
  searchInput: string = '';
  isLoading = false;
  isSearchFilterOn = false;
  stringsList = stringsList;
  isHovered = false;
  isHovered1 = false;
  isAdditionalDataBeingFetched = false;
  userID: string | null = null;
  private savedScrollPosition = 0;

  @ViewChild(MatSort) sort!: MatSort;
  @ViewChild('scrollableContainer') scrollableContainer!: ElementRef<HTMLElement>;

  constructor(
    public coaDataService: ChartOfAccountsDataService,
    public dialog: MatDialog,
    private snackBar: MatSnackBar,
    public translate: TranslateService,
    private input: ElementRef<HTMLInputElement>
  ) {
    const obs = fromEvent(this.input.nativeElement, 'keyup').pipe(
      map((e: any) => e.target.value),
      debounceTime(250),
      tap(() => { this.isSearchFilterOn = true; }),
      map((searchInput: string) => of(searchInput?.toLowerCase())),
      switchAll()
    );
    obs.subscribe(async (data) => {
      if (data.length) {
        await this.coaDataService.filterAccounts(this.userID as string, data).catch(console.error);
      } else {
        await this.coaDataService.initializeData(this.userID as string).catch(console.error);
        this.isSearchFilterOn = false;
      }
    });
  }

  ngOnInit(): void {
    this.isLoading = true;
    this.userID = localStorage.getItem('userID');
    this.coaDataService.initializeData(this.userID as string).then(() => {
      this.subs.sink = this.coaDataService.accounts.subscribe((data) => {
        this.dataSource = new MatTableDataSource<ChartOfAccountsModel>(data);
        this.dataSource.sort = this.sort;
        this.isLoading = false;
      });
    });
  }

  ngAfterViewInit(): void {
    if (this.dataSource) this.dataSource.sort = this.sort;
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
  }

  async onScroll(e: any) {
    if (this.isSearchFilterOn) return;
    const container = this.scrollableContainer.nativeElement;
    const remaining = container.scrollHeight - (container.scrollTop + container.clientHeight);
    this.savedScrollPosition = container.scrollTop;
    if (remaining < container.clientHeight * 0.9 && !this.isAdditionalDataBeingFetched) {
      this.isAdditionalDataBeingFetched = true;
      await this.coaDataService.fetchAdditional(this.userID as string).then(() => {
        this.isAdditionalDataBeingFetched = false;
      });
    }
  }

  addNewAccount(): void {
    const dialogRef = this.dialog.open(ChartOfAccountsAddDialogComponent, {
      width: '420px',
      data: { action: 'Add' },
    });
    dialogRef.afterClosed().subscribe((data) => {
      if (data) {
        this.snackBar.open('Account successfully added', '', {
          duration: 5000,
          panelClass: ['white-snackbar'],
        });
      }
    });
  }

  openBulkImport(): void {
    this.dialog.open(ChartOfAccountsBulkImportComponent, {
      width: '480px',
      height: '386px',
      hasBackdrop: true,
    });
  }

  editAccount(account: ChartOfAccountsModel): void {
    this.dialog.open(ChartOfAccountsAddDialogComponent, {
      width: '420px',
      data: { action: 'Edit', data: { ...account } },
    });
  }

  toggleCheckbox(account: ChartOfAccountsModel) {
    if (account.selected) {
      this.selectedInvoicesNum++;
    } else {
      this.selectedInvoicesNum--;
    }
  }

  toggleAllSelection(seed: boolean) {
    if (seed) {
      this.dataSource.data.forEach((obj) => { obj.selected = true; });
      this.selectedInvoicesNum = this.dataSource.data.length;
    } else {
      this.dataSource.data.forEach((obj) => { obj.selected = false; });
      this.selectedInvoicesNum = 0;
    }
  }

  async bulkDeleteAccounts() {
    const accountsForDelete = this.dataSource.data.filter((a: any) => a.selected);
    const plural = accountsForDelete.length > 1;
    const messages = plural
      ? {
          heading: 'Delete accounts?',
          contentPartOne: 'Are you sure you want to delete these accounts from chart of accounts?',
          action: 'Delete accounts',
        }
      : {
          heading: 'Delete account?',
          contentPartOne: 'Are you sure you want to delete this account from chart of accounts?',
          action: 'Delete account',
        };

    const dialogRef = this.dialog.open(SmallDialogComponent, {
      width: '460px',
      data: {
        heading: messages.heading,
        contentPartOne: messages.contentPartOne,
        action: messages.action,
      },
    });

    dialogRef.afterClosed().subscribe(async (response) => {
      if (response?.action === stringsList.doAction) {
        this.isLoading = true;
        const selected = this.dataSource.data.filter((a: any) => a.selected);
        await Promise.all(
          selected.map((a: any) =>
            this.coaDataService.deleteChartOfAccounts(a.id!).catch((err) => console.log(err))
          )
        );
        this.dataSource.data = this.dataSource.data.filter((a: any) => !a.selected);
        (this.coaDataService as any).dataStore.accounts = this.dataSource.data;
        this.selectedInvoicesNum = 0;
        this.snackBar.open(
          plural ? 'Selected accounts have been deleted' : 'Selected account has been deleted',
          'Dismiss',
          { duration: 5000, panelClass: ['white-snackbar'] }
        );
        this.isLoading = false;
      }
    });
  }

  deleteAccount(item: ChartOfAccountsModel): void {
    const dialogRef = this.dialog.open(SmallDialogComponent, {
      width: '460px',
      data: {
        heading: 'Delete account?',
        contentPartOne: 'Are you sure you want to delete account: ',
        contentFile: item.konto,
        contentPartTwo: "You can't undo this action.",
        action: 'Delete account',
      },
    });
    dialogRef.afterClosed().subscribe((response) => {
      if (response?.action === stringsList.doAction) {
        this.coaDataService.removeAccount(item);
      }
    });
  }
}
